/**
 * The API examples shown after a key is created (DX-1) and on /developers:
 * one source, so the copy-paste commands and the « Tester » button make the
 * same call. The simplest call is the first page of contacts
 * (POST /api/entities/ProspectQueue/page, cursor paging, docs/api/errors.md).
 */
import { BRAND } from '@/config/brand';

/** Environment variables the examples read: XDIAL_API_URL, XDIAL_API_KEY. */
export const ENV_PREFIX = BRAND.name.toUpperCase().replace(/[^A-Z0-9]/g, '_');
export const ENV_URL = `${ENV_PREFIX}_API_URL`;
export const ENV_KEY = `${ENV_PREFIX}_API_KEY`;

/** The absolute base URL of the API (VITE_API_URL, else /api on this origin), without trailing slash. */
export function apiBaseUrl(origin: string = typeof window !== 'undefined' ? window.location.origin : 'https://app.example.com'): string {
  const configured = (import.meta.env?.VITE_API_URL as string | undefined) ?? '/api';
  try {
    return new URL(configured, origin).href.replace(/\/+$/, '');
  } catch {
    return `${origin}/api`;
  }
}

export const CONTACTS_PATH = '/entities/ProspectQueue/page';
export const CALLS_PATH = '/entities/ParallelCall/page';
export const FIRST_PAGE_BODY = { limit: 5 } as const;

function shellQuote(s: string): string {
  return `'${s.replace(/'/g, `'\\''`)}'`;
}

/** The two `export` lines the examples read (QA-W7-14: the key on its own line, never inline in curl). */
export function exportEnv(base: string, key: string = 'rvk_...'): string {
  return `export ${ENV_URL}="${base}"\nexport ${ENV_KEY}="${key}"`;
}

/** curl for a paged read; with literal values, or with $XDIAL_API_URL / $XDIAL_API_KEY when omitted. */
export function curlPage(opts: { base?: string; key?: string; path?: string; body?: Record<string, unknown> } = {}): string {
  const url = opts.base ? `${opts.base}${opts.path ?? CONTACTS_PATH}` : `$${ENV_URL}${opts.path ?? CONTACTS_PATH}`;
  const auth = opts.key ? `Authorization: Bearer ${opts.key}` : `Authorization: Bearer $${ENV_KEY}`;
  return [
    `curl -s -X POST "${url}" \\`,
    `  -H "${auth}" \\`,
    '  -H "Content-Type: application/json" \\',
    `  -d ${shellQuote(JSON.stringify(opts.body ?? FIRST_PAGE_BODY))}`,
  ].join('\n');
}

/** Node 18+ (global fetch): one page of contacts. */
export function nodeFirstPage(path: string = CONTACTS_PATH, body: Record<string, unknown> = FIRST_PAGE_BODY): string {
  return [
    `const res = await fetch(\`\${process.env.${ENV_URL}}${path}\`, {`,
    "  method: 'POST',",
    `  headers: { Authorization: \`Bearer \${process.env.${ENV_KEY}}\`, 'Content-Type': 'application/json' },`,
    `  body: JSON.stringify(${JSON.stringify(body).replace(/"(\w+)":/g, '$1: ')}),`,
    '});',
    'const data = await res.json();',
    'if (!res.ok) throw new Error(`${res.status} ${data.error?.code ?? data.code}`);',
    'console.log(data.items.length, data.next_cursor);',
  ].join('\n');
}

/** Node 18+: every page, following next_cursor with the same filter and sort. */
export function nodeAllPages(path: string = CONTACTS_PATH): string {
  return [
    'async function* allRecords(filter = {}) {',
    '  let cursor;',
    '  do {',
    `    const res = await fetch(\`\${process.env.${ENV_URL}}${path}\`, {`,
    "      method: 'POST',",
    `      headers: { Authorization: \`Bearer \${process.env.${ENV_KEY}}\`, 'Content-Type': 'application/json' },`,
    "      body: JSON.stringify({ filter, sort: '-created_date', limit: 200, cursor }),",
    '    });',
    '    const page = await res.json();',
    '    if (!res.ok) throw new Error(`${res.status} ${page.error?.code ?? page.code}`);',
    '    yield* page.items;',
    '    cursor = page.next_cursor ?? undefined;',
    '  } while (cursor);',
    '}',
    '',
    'for await (const contact of allRecords()) console.log(contact.prospect_name);',
  ].join('\n');
}

export const CONTACTS_OUTPUT = JSON.stringify({
  items: [{ id: '6ac39fc34f710a3500ecd25f', prospect_name: 'Camille Martin', prospect_phone: '+33612345678', prospect_company: 'ACME', status: 'active', customer_id: '6ac39f...', created_date: '2026-10-30T09:12:44.000Z' }],
  next_cursor: 'eyJ2IjoxLCJrIjpbIjIwMjYtMTAtMzBUMDk6MTI6NDQuMDAwWiJd...',
}, null, 2);

// i18n-exempt: an API response example, English by contract
export const ERROR_OUTPUT = JSON.stringify({ error: { code: 'INVALID_CURSOR', message: 'The cursor is malformed, or was issued for another sort or filter' } }, null, 2);

export type ApiKeyTestResult =
  | { ok: true; status: number; json: string }
  | { ok: false; status: number | null; code: string };

/**
 * « Tester »: the first page of contacts with the key alone. The browser
 * session cookie is not sent (credentials: 'omit'), so the answer is what the
 * other product will get.
 */
export async function testApiKey(base: string, key: string, fetchImpl: typeof fetch = fetch): Promise<ApiKeyTestResult> {
  let res: Response;
  try {
    res = await fetchImpl(`${base}${CONTACTS_PATH}`, {
      method: 'POST',
      credentials: 'omit',
      headers: { Authorization: `Bearer ${key}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(FIRST_PAGE_BODY),
    });
  } catch {
    return { ok: false, status: null, code: 'NETWORK_ERROR' };
  }
  let body: unknown = null;
  try { body = await res.json(); } catch { /* not JSON */ }
  if (res.ok) return { ok: true, status: res.status, json: JSON.stringify(body, null, 2) };
  const b = body as { code?: string; error?: string | { code?: string } } | null;
  const code = (typeof b?.error === 'object' ? b.error?.code : undefined) ?? b?.code ?? `HTTP_${res.status}`;
  return { ok: false, status: res.status, code };
}
